import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck } from '@fortawesome/pro-light-svg-icons';

// services
import ValidationService from '../../../../../services/validation';
import OrderStatusService from '../../../../../services/orders/orderStatus';

// props
import { IOrderStatusResourceShortProps } from '../../../../../props/orders/orderStatus';

// components
import { DefaultButton, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';

interface IOrderStatusFormProps {
    orderStatus?: IOrderStatusResourceShortProps;
    onDismissed(refresh?: boolean): void;
}

type FormDataProps = {
    name: string;
    description: string;
    backgroundColor: string;
}

type FormDataErrorProps = {
    name?: string;
    description?: string;
    backgroundColor?: string;
}

const OrderStatusForm: React.FC<IOrderStatusFormProps> = (props: IOrderStatusFormProps) => {
    const { banner } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [data, setData] = React.useState<FormDataProps>({
        name: "",
        description: "",
        backgroundColor: ""
    });
    const [error, setError] = React.useState<FormDataErrorProps>({});
    const mode: 'create' | 'update' = props.orderStatus === undefined ? 'create' : 'update';

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        if (props.orderStatus) {
            setData({
                name: props.orderStatus.name,
                description: props.orderStatus.description,
                backgroundColor: props.orderStatus.backgroundColor
            });
        }

        setLoaded(true);
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (error.name || error.description || error.backgroundColor) {
            return true;
        } else if (data.name === '' || data.description === '' || data.backgroundColor === '') {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { name, description, backgroundColor } = data;

            // create form data
            let fd = new FormData();
            fd.append("name", name);
            fd.append("description", description);
            fd.append("backgroundColor", backgroundColor);

            if (props.orderStatus === undefined) {
                //await OrderStatusForm.new(fd);
            } else {
                await OrderStatusService.update(props.orderStatus.id, fd);
            }
            banner.add({
                key: 'create_order_status_success',
                variant: 'success',
                icon: faCheck,
                text: `Order status "${name}" ${mode === 'create' ? 'created' : 'updated'} successfully`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    return <Panel headerText={mode === 'create' ? 'Create Order Status' : 'Update Order Status'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={mode === 'create' ? "Creating order status ..." : "Updating order status ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: 20 } }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    <Stack.Item>
                        <TextField label={"Name"}
                            required={true}
                            value={data.name}
                            onChange={(evt, value) => {
                                data.name = value || "";
                                
                                const validation = ValidationService.combination(value, ['required', 'limit'], {maxChars: 100});
                                error.name = validation.message;

                                setData({...data});
                                setError({...error});
                            }}
                            errorMessage={error.name}
                            disabled={submitting} />
                    </Stack.Item>
                    <Stack.Item>
                        <TextField label={"Description"}
                            required={true}
                            multiline
                            rows={5}
                            autoAdjustHeight
                            resizable={false}
                            value={data.description}
                            onChange={(evt, value) => {
                                data.description = value || "";
                                
                                const validation = ValidationService.combination(value, ['required', 'limit'], {maxChars: 100});
                                error.description = validation.message;

                                setData({...data});
                                setError({...error});
                            }}
                            errorMessage={error.name}
                            disabled={submitting} />
                    </Stack.Item>
                    <Stack.Item>
                        <TextField label={"Background Color"}
                            required={true}
                            value={data.backgroundColor}
                            onChange={(evt, value) => {
                                data.backgroundColor = value || "";
                                
                                const validation = ValidationService.combination(value, ['required', 'limit'], {maxChars: 100});
                                error.backgroundColor = validation.message;

                                setData({...data});
                                setError({...error});
                            }}
                            errorMessage={error.name}
                            disabled={submitting} />
                    </Stack.Item>
                </> : null
            }
        </Stack>
    </Panel>
};

export default OrderStatusForm;
